import {
	AfterViewInit,
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
} from '@angular/core';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { Company } from 'src/app/models/data/company';
import { Role, RoleDataItem } from 'src/app/models/data/role';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { Mode, UserProfile } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../data-set/data-set.component';
import {
	DataSetTableComponent,
	DataSetTableMenuComponent,
} from '../data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatMenu, MatMenuContent, MatMenuItem } from '@angular/material/menu';
import { DataSetLoadingRowComponent } from '../data-set/data-set-loading-row.component';

@Component({
	selector: 'app-company-roles-table',
	templateUrl: './company-roles-table.component.html',
	styleUrls: [
		'./company-roles-table.component.css',
		'../../ui/common/main.scss',
	],
	imports: [
		DataSetComponent_1,
		DataSetToolbarComponent,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		DataSetTableMenuComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatMenu,
		MatMenuContent,
		MatMenuItem,
		DataSetLoadingRowComponent,
	],
})
export class CompanyRolesTableComponent
	implements AfterViewInit, OnDestroy, OnInit
{
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;

	@Input() roleDataRequest!: (filters: QueryParam[]) => Promise<RoleDataItem[]>;
	@Input() company?: Company;
	@Output() roleSelected: EventEmitter<{ role: Role; mode: Mode }> =
		new EventEmitter();
	@Input() companyId?: string;

	displayedColumns: string[] = [
		'roleName',
		'creatorName',
		'userCount',
		'rowMenu',
	];
	dataSource = new MatTableDataSource<RoleDataItem>();

	edit = Mode.edit;

	canAddRoles!: boolean;
	canEditRoles!: boolean;

	private filterSubscription?: Subscription;

	getMyRolesLoadingKey = ApiLoadingKeys.getMyRoles;
	getCompanyRolesByCompanyIdLoadingKey =
		ApiLoadingKeys.getCompanyRolesByCompanyId;
	finishedLoadingInitData: boolean = false;

	constructor(
		private readonly router: Router,
		private readonly scService: SystemCapabilityService,
		private readonly apiService: ApiService,
		public readonly loadingService: LoadingIndicatorService
	) {}

	async ngOnInit(): Promise<void> {
		const hasUserRoleManagement = this.scService.has(
			SystemCapabilityEnum.userRoleManagement
		);
		const hasGlobalCompanyMgmt = this.scService.has(
			SystemCapabilityEnum.globalCompanyManagement
		);
		const [sentrilockId, userProfile] = await Promise.all([
			this.apiService.getSentrilockCompanyId(),
			this.apiService.getUserProfile(),
		]);
		const activeCompanyId = userProfile?.activeRoleDetails?.company?.id;

		let editingCompanyId = this.company?.id;
		if (!editingCompanyId) {
			editingCompanyId = activeCompanyId;
		}

		const isEditingActiveCompany = activeCompanyId === editingCompanyId;
		const companyIsSentrilock = editingCompanyId === sentrilockId;

		this.canAddRoles =
			(hasUserRoleManagement && isEditingActiveCompany) ||
			(hasGlobalCompanyMgmt && !companyIsSentrilock);
		this.canEditRoles =
			(hasUserRoleManagement && isEditingActiveCompany) ||
			(hasGlobalCompanyMgmt && !companyIsSentrilock);
	}

	ngAfterViewInit(): void {
		this.dataSet.applyFilters();
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				mergeMap(async (filter) => {
					const result = await this.roleDataRequest(filter);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((roles) => {
				this.dataSource.data = roles;
			});
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.filterSubscription = undefined;
	}

	onRowSelected(role: Role, modeValue: Mode = Mode.viewOnly): void {
		const mode =
			role.isCompanyAdmin || role.isOracle ? Mode.viewOnly : modeValue;
		this.roleSelected.emit({ role, mode });
	}

	canClickRole(roleDataItem: RoleDataItem): boolean {
		// If user is not Oracle, prevent clicking on roles with audit logs capability
		const isOracle = this.scService.isOracle;
		if (!isOracle && roleDataItem.hasAuditLogsCapability) {
			return false;
		}
		return true;
	}

	navigateToAddRole(): void {
		this.router.navigate(['/add-role'], { state: { company: this.company } });
	}

	rowId = (row: RoleDataItem): string => {
		return row.role.id;
	};
}
